import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import type { ColumnConfig } from './types';
import type { EditingCell } from './useEditingState';
import { parseLocalizedNumber } from '@/lib/currency';
import {
  parseClipboardGrid,
  parseClipboardHtml,
  serializeClipboardGrid,
  serializeClipboardHtml,
} from './utils';

/**
 * Write a grid to the clipboard in BOTH `text/html` (a `<table>`) and
 * `text/plain` (quoted TSV). The HTML flavor is what lets multi-line cells
 * round-trip with Numbers/Excel/Sheets (they ignore TSV quotes); the TSV is
 * the fallback for plain-text targets. Degrades to `writeText` where the
 * async ClipboardItem API is unavailable. Errors are swallowed (clipboard
 * blocked / unavailable) — copy is best-effort.
 */
async function writeClipboard(rows: string[][]): Promise<void> {
  const text = serializeClipboardGrid(rows);
  try {
    if (
      typeof ClipboardItem !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.write === 'function'
    ) {
      const html = serializeClipboardHtml(rows);
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);
      return;
    }
  } catch {
    // fall through to text-only
  }
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Clipboard API unavailable
  }
}

/**
 * Read the clipboard as a grid, preferring the `text/html` `<table>` (keeps
 * spreadsheet multi-line cells intact) and falling back to quoted-TSV text.
 * Returns `null` when the clipboard holds nothing pasteable (empty, image
 * only, or unreadable) so the caller can NO-OP — critical, because treating
 * an unreadable clipboard as a single empty value would silently clear the
 * user's selected cells.
 */
async function readClipboard(): Promise<string[][] | null> {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.read === 'function') {
      const items = await navigator.clipboard.read();
      let html: string | null = null;
      let text = '';
      for (const item of items) {
        if (item.types.includes('text/html')) {
          html = await (await item.getType('text/html')).text();
        }
        if (item.types.includes('text/plain')) {
          text = await (await item.getType('text/plain')).text();
        }
      }
      const fromHtml = html ? parseClipboardHtml(html) : null;
      if (fromHtml) return fromHtml;
      // No usable HTML table: use plain text if any, else nothing to paste.
      // Return here (don't fall through to a second, redundant readText).
      return text ? parseClipboardGrid(text) : null;
    }
  } catch {
    // read() unavailable/failed — retry via the older readText API below.
  }
  try {
    const text = await navigator.clipboard.readText();
    return text ? parseClipboardGrid(text) : null;
  } catch {
    // Clipboard unreadable (permission denied / unavailable) — signal no-op.
    return null;
  }
}

export interface CellPosition {
  rowIndex: number;
  colIndex: number;
}

export interface SelectionRange {
  start: CellPosition;
  end: CellPosition;
}

interface UseKeyboardNavigationOptions {
  columns: ColumnConfig[];
  rowCount: number;
  editingCell: EditingCell | null;
  readOnly: boolean;
  /** Locale for parsing numeric paste values (e.g. 'de-DE' → comma is the
   *  decimal). Falls back to runtime default when omitted. */
  locale?: string;
  isRowSeparator: (rowIndex: number) => boolean;
  isRowReadOnly?: (rowIndex: number) => boolean;
  isColumnReadOnly: (colId: string, rowIndex: number) => boolean;
  /** Returns true if a column explicitly overrides row-level read-only for this row (readOnly === false, or a predicate returning false) */
  columnOverridesRowReadOnly?: (colId: string, rowIndex: number) => boolean;
  getCellValue: (rowIndex: number, colId: string) => unknown;
  setCellValue: (rowIndex: number, colId: string, value: unknown) => void;
  getRowId: (rowIndex: number) => string | undefined;
  onUndo: () => { rowId: string; field: string }[] | null;
  startUndoGroup: () => void;
  endUndoGroup: () => void;
  startEditing: (rowId: string, colId: string) => void;
  commitEdit: () => void;
  cancelEdit: () => void;
}

/** Get normalized min/max bounds from a selection range */
function getSelectionBounds(selection: SelectionRange) {
  return {
    minRow: Math.min(selection.start.rowIndex, selection.end.rowIndex),
    maxRow: Math.max(selection.start.rowIndex, selection.end.rowIndex),
    minCol: Math.min(selection.start.colIndex, selection.end.colIndex),
    maxCol: Math.max(selection.start.colIndex, selection.end.colIndex),
  };
}

export function useKeyboardNavigation({
  columns,
  rowCount,
  editingCell,
  readOnly,
  locale,
  isRowSeparator,
  isRowReadOnly,
  isColumnReadOnly,
  columnOverridesRowReadOnly,
  getCellValue,
  setCellValue,
  getRowId,
  onUndo,
  startUndoGroup,
  endUndoGroup,
  startEditing,
  commitEdit,
  cancelEdit,
}: UseKeyboardNavigationOptions) {
  // Combined cell-level read-only check: column readOnly === false overrides row-level
  const isCellEditable = useCallback((colId: string, rowIndex: number) => {
    if (isColumnReadOnly(colId, rowIndex)) return false;
    if (columnOverridesRowReadOnly?.(colId, rowIndex)) return true;
    if (isRowReadOnly?.(rowIndex)) return false;
    return true;
  }, [isColumnReadOnly, columnOverridesRowReadOnly, isRowReadOnly]);

  const focusedCellRef = useRef<CellPosition | null>(null);
  const [selection, setSelection] = useState<SelectionRange | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  // Stable refs for applyFocusClass to avoid recreation chain (Issue 6)
  const editingCellRef = useRef(editingCell);
  editingCellRef.current = editingCell;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  // Apply or remove the cell-focused CSS class directly on the DOM (no React re-render)
  // Uses data-attributes for O(1) DOM lookup (Issue 4) and refs for stable identity (Issue 6)
  const applyFocusClass = useCallback((cell: CellPosition | null) => {
    const table = tableRef.current;
    if (!table) return;

    // Remove from previous
    const prev = table.querySelector('.cell-focused');
    if (prev) prev.classList.remove('cell-focused');

    // Add to new (skip only when multi-cell selection is active)
    if (cell && !selectionRef.current) {
      const td = table.querySelector(`[data-row="${cell.rowIndex}"][data-col="${cell.colIndex}"]`) as HTMLElement | undefined;
      td?.classList.add('cell-focused');
    }
  }, []);

  // Update focused cell via ref + DOM class manipulation (no React re-render)
  const updateFocusedCell = useCallback((cell: CellPosition | null) => {
    focusedCellRef.current = cell;
    applyFocusClass(cell);
    // Scroll the focused cell into view
    if (cell) {
      const td = tableRef.current?.querySelector(`[data-row="${cell.rowIndex}"][data-col="${cell.colIndex}"]`) as HTMLElement | undefined;
      if (td) {
        const isSticky = td.classList.contains('cell-sticky');
        if (isSticky) {
          // Sticky cells are always horizontally visible — only scroll vertically.
          // scrollIntoView has no "inline: none" option, so preserve horizontal scroll position.
          const scrollParent = td.closest('.editable-table-scroll');
          const scrollLeft = scrollParent?.scrollLeft;
          td.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          if (scrollParent && scrollLeft !== undefined) {
            scrollParent.scrollLeft = scrollLeft;
          }
        } else {
          td.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }
    }
  }, [applyFocusClass]);

  // Re-apply focus class when editing, selection, or data changes
  // When editing ends, refocus the table container so keyboard nav continues working
  const prevEditingRef = useRef(editingCell);
  useEffect(() => {
    const wasEditing = prevEditingRef.current;
    prevEditingRef.current = editingCell;
    applyFocusClass(focusedCellRef.current);
    if (wasEditing && !editingCell) {
      endUndoGroup(); // close any open type-to-start undo group
      tableRef.current?.focus();
    }
  }, [applyFocusClass, editingCell, selection, endUndoGroup]);

  // Set focused cell and clear selection (single click)
  const moveTo = useCallback((cell: CellPosition) => {
    updateFocusedCell(cell);
    setSelection(null);
  }, [updateFocusedCell]);

  // Extend selection from anchor to new position (shift+click/arrow)
  const extendSelection = useCallback((to: CellPosition) => {
    const anchor = selectionRef.current?.start ?? focusedCellRef.current;
    if (!anchor) return;
    setSelection({ start: anchor, end: to });
    updateFocusedCell(to);
  }, [updateFocusedCell]);

  // Get the next valid row (skip separators)
  const getNextRow = useCallback((current: number, direction: 1 | -1): number => {
    let next = current + direction;
    while (next >= 0 && next < rowCount) {
      if (!isRowSeparator(next)) return next;
      next += direction;
    }
    return current;
  }, [rowCount, isRowSeparator]);

  // Get the next valid column (no wrapping)
  const getNextCol = useCallback((current: number, direction: 1 | -1): number => {
    const next = current + direction;
    if (next >= 0 && next < columns.length) {
      return next;
    }
    return current;
  }, [columns]);

  // Copy selection to the clipboard as an HTML table + quoted TSV, so
  // multi-line cells survive both in-app paste and spreadsheet paste.
  const handleCopy = useCallback(() => {
    if (!focusedCellRef.current) return;

    try {
      let rows: string[][];
      if (selectionRef.current) {
        const { minRow, maxRow, minCol, maxCol } = getSelectionBounds(selectionRef.current);
        rows = [];
        for (let r = minRow; r <= maxRow; r++) {
          if (isRowSeparator(r)) continue;
          const cells: string[] = [];
          for (let c = minCol; c <= maxCol; c++) {
            const col = columns[c];
            if (!col) continue;
            const val = getCellValue(r, col.data);
            cells.push(val == null ? '' : String(val));
          }
          rows.push(cells);
        }
      } else {
        const { rowIndex, colIndex } = focusedCellRef.current;
        const col = columns[colIndex];
        if (!col) return;
        const value = getCellValue(rowIndex, col.data);
        rows = [[value == null ? '' : String(value)]];
      }
      // Fire-and-forget within the user gesture; writeClipboard owns its errors.
      void writeClipboard(rows);
    } catch {
      // Reading cell values / selection bounds failed — abort copy silently.
    }
  }, [columns, getCellValue, isRowSeparator]);

  // Coerce a string value based on column type. Numeric values honor the
  // table's locale (same decimal/group rules as NumericCell / NumericCellEditor).
  const coerceValue = useCallback((text: string, col: ColumnConfig): unknown => {
    if (col.type === 'numeric') {
      return parseLocalizedNumber(text, locale);
    }
    if (col.type === 'checkbox') {
      return text === 'true' || text === '1';
    }
    return text;
  }, [locale]);

  // Paste clipboard into cells starting at focused cell, or fill selection if pasting a single value
  const handlePaste = useCallback(async () => {
    if (readOnly || !focusedCellRef.current) return;

    try {
      // Prefer the clipboard's HTML table (keeps multi-line spreadsheet cells
      // intact), falling back to quote-aware TSV. A quoted field keeps its
      // embedded newlines/tabs as one cell, so a multi-line cell no longer
      // spreads across rows.
      const lines = await readClipboard();
      // Nothing pasteable on the clipboard — leave cells untouched (never
      // clear a selection just because the clipboard was empty/unreadable).
      if (!lines) return;
      // Trim trailing empty line from clipboard (common with copy from spreadsheets)
      if (lines.length > 1 && lines[lines.length - 1].length === 1 && lines[lines.length - 1][0] === '') {
        lines.pop();
      }

      const isSingleValue = lines.length === 1 && lines[0].length === 1;

      // If pasting a single value into a multi-cell selection, fill all selected cells
      if (isSingleValue && selectionRef.current) {
        const { minRow, maxRow, minCol, maxCol } = getSelectionBounds(selectionRef.current);
        startUndoGroup();
        for (let r = minRow; r <= maxRow; r++) {
          if (isRowSeparator(r)) continue;
          for (let c = minCol; c <= maxCol; c++) {
            const col = columns[c];
            if (!col || isColumnReadOnly(col.data, r)) continue;
            const value = coerceValue(lines[0][0], col);
            setCellValue(r, col.data, value);
          }
        }
        endUndoGroup();
        return;
      }

      // Default behavior: paste grid starting at focused cell
      const startRow = focusedCellRef.current.rowIndex;
      const startCol = focusedCellRef.current.colIndex;

      startUndoGroup();
      let dataRowOffset = 0;
      for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
        // Find the next non-separator row
        let targetRow = startRow + dataRowOffset;
        while (targetRow < rowCount && isRowSeparator(targetRow)) {
          dataRowOffset++;
          targetRow = startRow + dataRowOffset;
        }
        if (targetRow >= rowCount) break;

        const cells = lines[lineIdx];
        for (let cellIdx = 0; cellIdx < cells.length; cellIdx++) {
          const targetCol = startCol + cellIdx;
          if (targetCol >= columns.length) break;
          const col = columns[targetCol];
          if (!col || isColumnReadOnly(col.data, targetRow)) continue;

          const value = coerceValue(cells[cellIdx], col);
          setCellValue(targetRow, col.data, value);
        }
        dataRowOffset++;
      }
      endUndoGroup();

      // Select the pasted range
      const lastRow = Math.min(startRow + dataRowOffset - 1, rowCount - 1);
      const lastCol = Math.min(startCol + (lines[0]?.length ?? 1) - 1, columns.length - 1);
      if (dataRowOffset > 1 || (lines[0]?.length ?? 0) > 1) {
        setSelection({
          start: { rowIndex: startRow, colIndex: startCol },
          end: { rowIndex: lastRow, colIndex: lastCol },
        });
      }
    } catch {
      // Clipboard access denied — ignore
    }
  }, [columns, readOnly, setCellValue, rowCount, isRowSeparator, isColumnReadOnly, coerceValue, startUndoGroup, endUndoGroup]);

  // --- Sub-handlers for handleKeyDown (Issue 11) ---

  /** Handle global shortcuts: Ctrl+C/V/Z/A */
  const handleGlobalKeyDown = useCallback((e: KeyboardEvent): boolean => {
    if (!(e.metaKey || e.ctrlKey) || editingCellRef.current) return false;

    switch (e.key) {
      case 'c':
        e.preventDefault();
        handleCopy();
        return true;
      case 'v':
        e.preventDefault();
        handlePaste();
        return true;
      case 'z': {
        e.preventDefault();
        const undone = onUndo();
        if (undone && undone.length > 0) {
          // Focus the last undone cell (most recent change being reverted)
          const last = undone[undone.length - 1];
          const colIdx = columns.findIndex(c => c.data === last.field);
          let targetRow = -1;
          for (let i = 0; i < rowCount; i++) {
            if (getRowId(i) === last.rowId) { targetRow = i; break; }
          }
          if (targetRow >= 0 && colIdx >= 0) {
            moveTo({ rowIndex: targetRow, colIndex: colIdx });
            // Re-apply focus after React re-renders (undo changes data → DOM recreated)
            requestAnimationFrame(() => applyFocusClass(focusedCellRef.current));
          }
        }
        return true;
      }
      case 'a': {
        e.preventDefault();
        const firstDataRow = Array.from({ length: rowCount }, (_, i) => i).find(i => !isRowSeparator(i));
        if (firstDataRow != null) {
          setSelection({
            start: { rowIndex: firstDataRow, colIndex: 0 },
            end: { rowIndex: rowCount - 1, colIndex: columns.length - 1 },
          });
          updateFocusedCell({ rowIndex: firstDataRow, colIndex: 0 });
        }
        return true;
      }
      default:
        return false;
    }
  }, [handleCopy, handlePaste, onUndo, rowCount, isRowSeparator, columns, getRowId, moveTo, applyFocusClass, updateFocusedCell]);

  /** Handle keys while in editing mode: Escape, Tab, Enter */
  const handleEditingKeyDown = useCallback((e: KeyboardEvent, rowIndex: number, colIndex: number): void => {
    // Don't intercept keys when focus is inside a portal (popover/dropdown editor).
    // Radix portals render outside the table div, so if the event target is not
    // inside the table container, the event came from a portal — let it through.
    const target = e.target as HTMLElement;
    const table = tableRef.current;
    if (table && !table.contains(target)) {
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        cancelEdit();
        break;
      case 'Tab': {
        e.preventDefault();
        commitEdit();
        endUndoGroup(); // close any open type-to-start undo group
        const direction = e.shiftKey ? -1 : 1;
        // Tab wrapping at column edges (Issue 13)
        let nextCol = colIndex + direction;
        let nextRow = rowIndex;
        if (nextCol >= columns.length) {
          nextCol = 0;
          nextRow = getNextRow(rowIndex, 1);
        } else if (nextCol < 0) {
          nextCol = columns.length - 1;
          nextRow = getNextRow(rowIndex, -1);
        }
        moveTo({ rowIndex: nextRow, colIndex: nextCol });
        if (isCellEditable(columns[nextCol]?.data, nextRow)) {
          const rid = getRowId(nextRow);
          if (rid) startEditing(rid, columns[nextCol]?.data);
        }
        break;
      }
      case 'Enter':
        // Enter is handled by the cell editor itself (flush local value + commit).
        // We only move focus to the next row here; the cell editor stops propagation
        // for its own Enter handling, so this only runs for editors that don't.
        if (!e.shiftKey) {
          e.preventDefault();
          const nextRow = getNextRow(rowIndex, 1);
          moveTo({ rowIndex: nextRow, colIndex });
        }
        break;
    }
  }, [columns, cancelEdit, commitEdit, endUndoGroup, getNextRow, moveTo, isCellEditable, getRowId, startEditing]);

  /** Handle keys in navigation mode: arrows, Tab, Enter/F2, Delete, Escape */
  const handleNavigationKeyDown = useCallback((e: KeyboardEvent, rowIndex: number, colIndex: number): void => {
    const col = columns[colIndex];

    switch (e.key) {
      case 'ArrowUp': {
        e.preventDefault();
        const next = { rowIndex: getNextRow(rowIndex, -1), colIndex };
        if (e.shiftKey) { extendSelection(next); } else { moveTo(next); }
        break;
      }
      case 'ArrowDown': {
        e.preventDefault();
        const next = { rowIndex: getNextRow(rowIndex, 1), colIndex };
        if (e.shiftKey) { extendSelection(next); } else { moveTo(next); }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const next = { rowIndex, colIndex: getNextCol(colIndex, -1) };
        if (e.shiftKey) { extendSelection(next); } else { moveTo(next); }
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        const next = { rowIndex, colIndex: getNextCol(colIndex, 1) };
        if (e.shiftKey) { extendSelection(next); } else { moveTo(next); }
        break;
      }
      case 'Tab': {
        e.preventDefault();
        // Tab wrapping at column edges (Issue 13)
        const direction = e.shiftKey ? -1 : 1;
        let nextCol = colIndex + direction;
        let nextRow = rowIndex;
        if (nextCol >= columns.length) {
          nextCol = 0;
          nextRow = getNextRow(rowIndex, 1);
        } else if (nextCol < 0) {
          nextCol = columns.length - 1;
          nextRow = getNextRow(rowIndex, -1);
        }
        moveTo({ rowIndex: nextRow, colIndex: nextCol });
        break;
      }
      case 'Enter':
      case 'F2':
        if (!readOnly && col && isCellEditable(col.data, rowIndex)) {
          e.preventDefault();
          const rid = getRowId(rowIndex);
          if (rid) startEditing(rid, col.data);
        }
        break;
      case 'Delete':
      case 'Backspace':
        if (!readOnly) {
          e.preventDefault();
          if (selectionRef.current) {
            // Clear all selected cells as a single undo group
            const { minRow, maxRow, minCol, maxCol } = getSelectionBounds(selectionRef.current);
            startUndoGroup();
            for (let r = minRow; r <= maxRow; r++) {
              if (isRowSeparator(r) || isRowReadOnly?.(r)) continue;
              for (let c = minCol; c <= maxCol; c++) {
                const targetCol = columns[c];
                if (targetCol && !isColumnReadOnly(targetCol.data, r)) {
                  setCellValue(r, targetCol.data, null);
                }
              }
            }
            endUndoGroup();
          } else if (col && isCellEditable(col.data, rowIndex)) {
            setCellValue(rowIndex, col.data, null);
          }
        }
        break;
      case ' ':
        if (!readOnly && col?.type === 'checkbox' && isCellEditable(col.data, rowIndex)) {
          e.preventDefault();
          const current = getCellValue(rowIndex, col.data);
          setCellValue(rowIndex, col.data, !current);
        }
        break;
      case 'Escape':
        if (selectionRef.current) {
          e.preventDefault();
          setSelection(null);
        }
        break;
      default:
        // Printable character → clear cell and start editing with the typed character
        if (
          !readOnly &&
          !e.metaKey && !e.ctrlKey && !e.altKey &&
          e.key.length === 1 &&
          col && isCellEditable(col.data, rowIndex) &&
          (col.type === 'text' || col.type === 'numeric' || (col.type === 'time' && /\d/.test(e.key)))
        ) {
          const rid = getRowId(rowIndex);
          if (rid) {
            e.preventDefault();
            // Group the type-to-start clear + editor commit as a single undo
            startUndoGroup();
            setCellValue(rowIndex, col.data, e.key);
            startEditing(rid, col.data);
          }
        }
        break;
    }
  }, [columns, readOnly, isCellEditable, isColumnReadOnly, getRowId, getCellValue, startEditing, getNextRow, getNextCol, moveTo, extendSelection, setCellValue, isRowSeparator, isRowReadOnly, startUndoGroup, endUndoGroup]);

  // Main key handler — dispatches to sub-handlers.
  // Returned as a React onKeyDown handler so child editors' onKeyDown fires first
  // (React synthetic events fire child-before-parent), ensuring editors flush their
  // local value before the navigation handler commits and moves focus.
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const nativeEvent = e.nativeEvent;
    const target = nativeEvent.target as HTMLElement | null;
    // Keystrokes from a PORTAL rendered by something inside the table (a
    // dialog, popover or menu opened from a cell or an expanded row) bubble
    // here through the React tree although their DOM node sits outside the
    // <table> — they belong to that portal, never to the grid (no Ctrl+C/V/A/Z
    // hijack, no cell navigation). Every in-table event is unaffected.
    const table = tableRef.current;
    if (table && target instanceof Node && !table.contains(target)) return;
    // Keystrokes inside an expanded detail panel belong to ITS inputs — the
    // panel lives inside the <Table>, so without this guard a lingering
    // focused cell would swallow the typing and edit the parent row instead.
    if (target?.closest('.expanded-detail-content')) return;
    if (handleGlobalKeyDown(nativeEvent)) return;
    if (!focusedCellRef.current) return;

    const { rowIndex, colIndex } = focusedCellRef.current;

    if (editingCellRef.current) {
      handleEditingKeyDown(nativeEvent, rowIndex, colIndex);
    } else {
      handleNavigationKeyDown(nativeEvent, rowIndex, colIndex);
    }
  }, [handleGlobalKeyDown, handleEditingKeyDown, handleNavigationKeyDown]);

  // Public API to set focused cell (from click)
  const setFocusedCell = useCallback((rowIndex: number, colIndex: number, shiftKey?: boolean) => {
    if (shiftKey) {
      extendSelection({ rowIndex, colIndex });
    } else {
      moveTo({ rowIndex, colIndex });
    }
  }, [moveTo, extendSelection]);

  // Pre-compute selection class map — O(1) lookup per cell
  const selectionClassMap = useMemo(() => {
    if (!selection) return new Map<string, string>();
    const map = new Map<string, string>();
    const { minRow, maxRow, minCol, maxCol } = getSelectionBounds(selection);
    for (let r = minRow; r <= maxRow; r++) {
      if (isRowSeparator(r)) continue;

      // Top edge: this row is minRow, or every row between minRow and this row is a separator
      let isTopEdge = r === minRow;
      if (!isTopEdge) {
        isTopEdge = true;
        for (let rr = minRow; rr < r; rr++) {
          if (!isRowSeparator(rr)) { isTopEdge = false; break; }
        }
      }

      // Bottom edge: this row is maxRow, or every row between this row and maxRow is a separator
      let isBottomEdge = r === maxRow;
      if (!isBottomEdge) {
        isBottomEdge = true;
        for (let rr = r + 1; rr <= maxRow; rr++) {
          if (!isRowSeparator(rr)) { isBottomEdge = false; break; }
        }
      }

      for (let c = minCol; c <= maxCol; c++) {
        const classes = ['cell-selected'];
        if (isTopEdge) classes.push('sel-top');
        if (isBottomEdge) classes.push('sel-bottom');
        if (c === minCol) classes.push('sel-left');
        if (c === maxCol) classes.push('sel-right');
        map.set(`${r}:${c}`, classes.join(' '));
      }
    }
    return map;
  }, [selection, isRowSeparator]);

  // Apply selection classes directly on the DOM (like applyFocusClass) to avoid re-rendering all rows
  const prevSelectionMapRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    // Remove old selection classes
    for (const [key, classes] of prevSelectionMapRef.current) {
      const [r, c] = key.split(':');
      const td = table.querySelector(`[data-row="${r}"][data-col="${c}"]`);
      if (td) {
        for (const cls of classes.split(' ')) {
          td.classList.remove(cls);
        }
      }
    }

    // Add new selection classes
    for (const [key, classes] of selectionClassMap) {
      const [r, c] = key.split(':');
      const td = table.querySelector(`[data-row="${r}"][data-col="${c}"]`);
      if (td) {
        for (const cls of classes.split(' ')) {
          td.classList.add(cls);
        }
      }
    }

    prevSelectionMapRef.current = selectionClassMap;
  }, [selectionClassMap]);

  // Stable identity — returns empty string since selection is now DOM-managed
  const getCellSelectionClasses = useCallback((_rowIndex: number, _colIndex: number): string => {
    return '';
  }, []);

  /** Drop cell focus + selection entirely — e.g. when the user clicks into an
   * expanded detail panel, so the parent grid stops looking (and acting)
   * selected. */
  const clearCellFocus = useCallback(() => {
    updateFocusedCell(null);
    setSelection(null);
  }, [updateFocusedCell]);

  return {
    tableRef,
    handleKeyDown,
    selection,
    setFocusedCell,
    clearCellFocus,
    getCellSelectionClasses,
  };
}
